import { useState } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertTriangle } from 'lucide-react'
import { useAlerts, useActOnAlert, type ClinicalAlert } from '@/features/insights/hooks/use-insights'
import { InsightCard } from '@/features/insights/components/InsightCard'
import { InsightModal } from '@/features/insights/components/InsightModal'
import { DismissDropdown } from '@/features/insights/components/DismissDropdown'

interface PatientInsightsTabProps {
  patientId: string
}

export function PatientInsightsTab({ patientId }: PatientInsightsTabProps) {
  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null)
  const [dismissingAlert, setDismissingAlert] = useState<ClinicalAlert | null>(null)

  const { data: alerts, isLoading } = useAlerts({ status: 'all', patientId })
  const actOnAlert = useActOnAlert()

  const activeAlerts = (alerts ?? []).filter((a) => a.status === 'active')
  const resolvedAlerts = (alerts ?? []).filter((a) => a.status !== 'active')

  if (isLoading) {
    return <InsightsSkeleton />
  }

  if (!alerts || alerts.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <AlertTriangle className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p className="text-lg font-medium">Nenhum alerta encontrado</p>
        <p className="text-sm mt-1">
          Os alertas são gerados automaticamente a partir dos relatos
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {activeAlerts.length > 0 && (
        <div>
          <h3 className="text-lg font-medium mb-4">Alertas Ativos</h3>
          <div className="space-y-4">
            {activeAlerts.map((alert) => (
              <InsightCard
                key={alert.id}
                alert={alert}
                onViewDetails={(id) => setSelectedAlertId(id)}
                onDismiss={(a) => setDismissingAlert(a)}
                isActing={actOnAlert.isPending}
              />
            ))}
          </div>
        </div>
      )}

      {resolvedAlerts.length > 0 && (
        <div>
          <h3 className="text-lg font-medium mb-4 text-muted-foreground">Resolvidos</h3>
          <div className="space-y-4 opacity-75">
            {resolvedAlerts.map((alert) => (
              <InsightCard
                key={alert.id}
                alert={alert}
                onViewDetails={(id) => setSelectedAlertId(id)}
              />
            ))}
          </div>
        </div>
      )}

      <InsightModal
        insightId={selectedAlertId}
        open={!!selectedAlertId}
        onOpenChange={(open) => !open && setSelectedAlertId(null)}
      />

      {dismissingAlert && (
        <DismissDropdown
          alert={dismissingAlert}
          open={!!dismissingAlert}
          onOpenChange={(open) => !open && setDismissingAlert(null)}
          onConfirm={(reason, note) => {
            actOnAlert.mutate({
              insightId: dismissingAlert.id,
              actionType: 'dismissed',
              dismissReason: reason,
              note,
            })
            setDismissingAlert(null)
          }}
          isSubmitting={actOnAlert.isPending}
        />
      )}
    </div>
  )
}

function InsightsSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="card p-4">
          <div className="flex items-start gap-3">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <div className="flex-1">
              <Skeleton className="h-4 w-48 mb-2" />
              <Skeleton className="h-12 w-full" />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
